import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Lock, Clock, BadgeCheck, ShieldCheck } from 'lucide-react';
import PersonaCard from '../components/PersonaCard.jsx';
import LanguageSwitch from '../components/LanguageSwitch.jsx';
import { PERSONAS, findPersona } from '../lib/personas.js';
import { t } from '../lib/i18n.js';
import {
  captureAttribution,
  trackPersonaSelected,
  grantConsent,
  trackCallbackRequested
} from '../lib/analytics.js';
import { readSession, writeSession } from '../lib/session.js';
import { requestCallback } from '../lib/api.js';

const PHONE_RE = /^[+]?[\d\s().-]{10,20}$/;
const NAME_RE = /^[A-Za-z][A-Za-z'\- ]{0,39}$/;

export default function Start() {
  const navigate = useNavigate();
  const [lang, setLang] = useState('en');
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [prefill, setPrefill] = useState({ firstName: '', phone: '' });
  const formRef = useRef(null);

  useEffect(() => {
    captureAttribution();
    // Recruit-study deep-link: ?study=<id>&ph=<phone>&fn=<first_name> arrives
    // from the screener form. Persist studyId so it survives navigation; the
    // phone + first name are pre-fill hints for the form below.
    const params = new URLSearchParams(window.location.search);
    const study = params.get('study');
    if (study) {
      writeSession({
        studyId: study,
        prefillPhone: params.get('ph') || undefined,
        prefillFirstName: params.get('fn') || undefined
      });
    }
    const s = readSession();
    if (s.language) setLang(s.language);
    if (s.personaId) setSelected(s.personaId);
    if (s.prefillPhone || s.prefillFirstName) {
      setPrefill({
        firstName: s.prefillFirstName || '',
        phone: s.prefillPhone || ''
      });
    }
  }, []);

  const persona = selected ? findPersona(selected) : null;

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    reset
  } = useForm({ mode: 'onChange', defaultValues: { firstName: '', phone: '', callConsent: false, textConsent: false } });

  // When deep-linked from the recruit screener, pre-fill once prefill state lands.
  useEffect(() => {
    if (prefill.firstName || prefill.phone) {
      reset({ firstName: prefill.firstName, phone: prefill.phone, callConsent: false, textConsent: false });
    }
  }, [prefill.firstName, prefill.phone, reset]);

  const onSelect = (p) => {
    setSelected(p.id);
    trackPersonaSelected(p);
    writeSession({ personaId: p.id, language: lang });
    // Preserve recruit-study prefill when switching personas; otherwise blank.
    reset({
      firstName: prefill.firstName || '',
      phone: prefill.phone || '',
      callConsent: false,
      textConsent: false
    });
    // Smoothly scroll the form into view after it mounts.
    requestAnimationFrame(() => {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  };

  const onSubmit = async (values) => {
    if (!selected) return;
    setApiError(null);
    setSubmitting(true);
    try {
      grantConsent();
      trackCallbackRequested({ personaId: selected, languageCode: lang });
      const session = readSession();
      await requestCallback({
        personaId: selected,
        phone: values.phone,
        languageCode: lang,
        callConsent: values.callConsent,
        textConsent: values.textConsent,
        consentAt: new Date().toISOString(),
        firstName: values.firstName.trim(),
        studyId: session.studyId
      });
      writeSession({
        personaId: selected,
        language: lang,
        firstName: values.firstName.trim(),
        submittedAt: new Date().toISOString()
      });
      navigate('/connect');
    } catch (err) {
      setApiError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* ─── COMING-SOON OVERLAY ─────────────────────────────────────────
          Pre-launch lockout: covers the entire viewport so no one can
          start an AI conversation until we officially launch. To remove
          when launching, delete this whole block (everything between the
          opening and closing tags of this div). The rest of the page
          remains intact underneath. */}
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-vita-navy/22 backdrop-blur-[6px] px-6">
        <div className="max-w-lg text-center">
          <div className="flex items-baseline justify-center leading-none">
            <span className="font-serif text-3xl tracking-tight text-stone-50">Vita</span>
            <span className="font-serif text-3xl tracking-tight text-vita-accent">MD</span>
          </div>
          <h1 className="mt-10 font-serif text-5xl md:text-7xl text-stone-50 leading-none">
            Coming soon.
          </h1>
          <p className="mt-6 text-stone-300 text-lg leading-relaxed">
            Alfred isn’t taking calls yet. We’re putting the finishing touches
            on the experience and will open the line shortly.
          </p>
          <a
            href="/"
            className="mt-10 inline-block text-sm text-vita-accent hover:text-vita-bright transition border-b border-vita-accent/40 hover:border-vita-bright pb-0.5"
          >
            ← Back to home
          </a>
        </div>
      </div>

      {/* ─── Hero + Persona picker ─────────────────────────────────────── */}
      <section className="mx-auto max-w-6xl px-6 py-12 md:py-20">
        <div className="max-w-3xl">
          <p className="text-xs uppercase tracking-[0.3em] text-alfred-gold">
            {t(lang, 'heroEyebrow')}
          </p>
          <h1 className="mt-4 text-4xl md:text-6xl font-serif leading-tight">
            {t(lang, 'heroTitle')}
          </h1>
          <p className="mt-5 text-lg text-stone-300 max-w-xl">
            {t(lang, 'heroSub')}
          </p>
          <div className="mt-6">
            <LanguageSwitch
              value={lang}
              onChange={(c) => {
                setLang(c);
                writeSession({ language: c });
              }}
            />
          </div>
        </div>

        <div className="mt-12">
          <h2 className="text-sm uppercase tracking-[0.25em] text-stone-400 mb-4">
            {t(lang, 'chooseVoice')}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {PERSONAS.map((p) => (
              <PersonaCard
                key={p.id}
                persona={p}
                lang={lang}
                selected={selected === p.id}
                onSelect={onSelect}
              />
            ))}
          </div>
        </div>

        {/* ─── Inline form — only visible once a persona is picked ───── */}
        {persona && (
          <section ref={formRef} className="mt-14 max-w-xl">
            <p className="text-xs uppercase tracking-[0.25em] text-alfred-gold">
              {persona.name}
            </p>
            <h2 className="mt-2 text-2xl md:text-3xl font-serif leading-tight">
              {t(lang, 'journeyTitle')}
            </h2>
            <p className="mt-3 text-stone-300">{t(lang, 'journeySub')}</p>

            <form onSubmit={handleSubmit(onSubmit)} className="mt-6 space-y-5">
              <div>
                <label className="field-label" htmlFor="firstName">
                  {t(lang, 'firstName')}
                </label>
                <input
                  id="firstName"
                  type="text"
                  autoComplete="given-name"
                  placeholder={t(lang, 'firstNamePh')}
                  className="field-input"
                  {...register('firstName', {
                    required: true,
                    pattern: NAME_RE,
                    maxLength: 40
                  })}
                />
                {errors.firstName && (
                  <p className="mt-1 text-sm text-red-400">
                    Please enter a valid first name.
                  </p>
                )}
              </div>

              <div>
                <label className="field-label" htmlFor="phone">
                  {t(lang, 'phone')}
                </label>
                <input
                  id="phone"
                  type="tel"
                  autoComplete="tel"
                  inputMode="tel"
                  placeholder={t(lang, 'phonePh')}
                  className="field-input"
                  {...register('phone', { required: true, pattern: PHONE_RE })}
                />
                {errors.phone && (
                  <p className="mt-1 text-sm text-red-400">
                    Please enter a valid phone number.
                  </p>
                )}
              </div>

              <label className="flex items-start gap-3 text-sm text-stone-300">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4 rounded border-stone-600 bg-stone-900 text-alfred-gold focus:ring-alfred-gold"
                  {...register('callConsent', { required: true })}
                />
                <span>{t(lang, 'callConsentLabel')}</span>
              </label>
              {errors.callConsent && (
                <p className="-mt-2 text-sm text-red-400">
                  We can't call without your consent.
                </p>
              )}

              <label className="flex items-start gap-3 text-sm text-stone-300">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4 rounded border-stone-600 bg-stone-900 text-alfred-gold focus:ring-alfred-gold"
                  {...register('textConsent')}
                />
                <span>{t(lang, 'textConsentLabel')}</span>
              </label>

              {apiError && (
                <div className="rounded-lg border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-200">
                  {apiError}
                </div>
              )}

              <button
                type="submit"
                className="btn-primary w-full sm:w-auto"
                disabled={!isValid || submitting}
              >
                {submitting ? t(lang, 'submitting') : t(lang, 'submit')}
              </button>
            </form>
          </section>
        )}
      </section>

      {/* ─── Credibility strip — always visible ─────────────────────── */}
      <section id="trust" className="border-y border-stone-800 bg-stone-950/60">
        <div className="mx-auto max-w-6xl px-6 py-16">
          <p className="text-xs uppercase tracking-[0.3em] text-alfred-gold">
            {t(lang, 'credibilityEyebrow')}
          </p>
          <h2 className="mt-3 text-3xl md:text-4xl font-serif leading-tight max-w-2xl">
            {t(lang, 'credibilityTitle')}
          </h2>

          <div className="mt-10 grid gap-6 sm:grid-cols-3">
            <TrustBlock
              icon={Lock}
              title={t(lang, 'trustPrivate')}
              body={t(lang, 'trustPrivateBody')}
            />
            <TrustBlock
              icon={Clock}
              title={t(lang, 'trust247')}
              body={t(lang, 'trust247Body')}
            />
            <TrustBlock
              icon={BadgeCheck}
              title={t(lang, 'trustLicensed')}
              body={t(lang, 'trustLicensedBody')}
            />
          </div>

          <div className="mt-14 rounded-2xl border border-stone-800 bg-stone-900/40 px-6 py-5 flex items-start gap-4">
            <ShieldCheck className="h-6 w-6 shrink-0 text-alfred-gold" aria-hidden="true" />
            <p className="text-sm text-stone-300 leading-relaxed">
              {t(lang, 'regulatoryBlurb')}
            </p>
          </div>
        </div>
      </section>

      {/* ─── Testimonials ───────────────────────────────────────────── */}
      <section id="stories" className="mx-auto max-w-6xl px-6 py-16 md:py-20">
        <h2 className="text-sm uppercase tracking-[0.25em] text-stone-400">
          {t(lang, 'testimonialsTitle')}
        </h2>
        <div className="mt-8 grid gap-6 sm:grid-cols-3">
          <TestimonialCard
            name={t(lang, 'testimonial1Name')}
            text={t(lang, 'testimonial1Text')}
          />
          <TestimonialCard
            name={t(lang, 'testimonial2Name')}
            text={t(lang, 'testimonial2Text')}
          />
          <TestimonialCard
            name={t(lang, 'testimonial3Name')}
            text={t(lang, 'testimonial3Text')}
          />
        </div>
      </section>

      {/* ─── How it works ────────────────────────────────────────────── */}
      <section id="how-it-works" className="mx-auto max-w-6xl px-6 pb-20">
        <h2 className="text-sm uppercase tracking-[0.25em] text-stone-400">
          How it works
        </h2>
        <div className="mt-6 grid md:grid-cols-3 gap-6">
          {[
            { n: '01', t: 'Choose a voice', d: 'Pick the persona that sounds like you.' },
            { n: '02', t: 'Share your number', d: 'Alfred calls within 60 seconds, any hour.' },
            { n: '03', t: 'A licensed provider takes over', d: 'Prescriptions, pharmacy, shipped to your door.' }
          ].map((s) => (
            <div key={s.n} className="card">
              <p className="text-alfred-gold font-serif text-xl">{s.n}</p>
              <p className="mt-2 font-medium text-stone-100">{s.t}</p>
              <p className="mt-1 text-sm text-stone-400">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ─── Programs / pricing ──────────────────────────────────────── */}
      <section id="programs" className="border-y border-stone-800 bg-stone-950/60">
        <div className="mx-auto max-w-6xl px-6 py-16">
          <p className="text-xs uppercase tracking-[0.3em] text-alfred-gold">
            {t(lang, 'plansEyebrow')}
          </p>
          <h2 className="mt-3 text-3xl md:text-5xl font-serif leading-tight">
            {t(lang, 'plansTitle')}
          </h2>
          <p className="mt-4 max-w-2xl text-stone-300">
            {t(lang, 'plansSub')}
          </p>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            <PlanCard
              name={t(lang, 'planPerformanceName')}
              price="$79"
              cadence={t(lang, 'planCadence')}
              tag={t(lang, 'planPerformanceTag')}
              body={t(lang, 'planPerformanceBody')}
            />
            <PlanCard
              name={t(lang, 'planStrengthName')}
              price="$199"
              cadence={t(lang, 'planCadence')}
              tag={t(lang, 'planStrengthTag')}
              body={t(lang, 'planStrengthBody')}
            />
            <PlanCard
              name={t(lang, 'planResetName')}
              price="$279"
              cadence={t(lang, 'planCadence')}
              tag={t(lang, 'planResetTag')}
              body={t(lang, 'planResetBody')}
            />
            <PlanCard
              name={t(lang, 'planTotalName')}
              price="$399"
              cadence={t(lang, 'planCadence')}
              tag={t(lang, 'planTotalTag')}
              body={t(lang, 'planTotalBody')}
              featured
            />
          </div>
          <p className="mt-8 text-sm text-stone-400 max-w-3xl leading-relaxed">
            {t(lang, 'plansFooter')}
          </p>
        </div>
      </section>

      {/* ─── Differentiation ─────────────────────────────────────────── */}
      <section id="why-this" className="mx-auto max-w-6xl px-6 py-16 md:py-20">
        <p className="text-xs uppercase tracking-[0.3em] text-alfred-gold">
          {t(lang, 'diffEyebrow')}
        </p>
        <h2 className="mt-3 text-3xl md:text-4xl font-serif leading-tight max-w-2xl">
          {t(lang, 'diffTitle')}
        </h2>
        <div className="mt-10 grid gap-6 md:grid-cols-3">
          <DiffCompare
            highlight={t(lang, 'diffHims')}
            body={t(lang, 'diffHimsBody')}
          />
          <DiffCompare
            highlight={t(lang, 'diffRo')}
            body={t(lang, 'diffRoBody')}
          />
          <DiffCompare
            highlight={t(lang, 'diffOthers')}
            body={t(lang, 'diffOthersBody')}
          />
        </div>
      </section>

      {/* ─── About ───────────────────────────────────────────────────── */}
      <section id="about" className="border-y border-stone-800 bg-stone-950/60">
        <div className="mx-auto max-w-6xl px-6 py-16 md:py-20">
          <div className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.3em] text-alfred-gold">
              {t(lang, 'aboutEyebrow')}
            </p>
            <h2 className="mt-3 text-3xl md:text-4xl font-serif leading-tight">
              {t(lang, 'aboutTitle')}
            </h2>
            <p className="mt-5 text-stone-300 leading-relaxed">
              {t(lang, 'aboutBody')}
            </p>
          </div>
        </div>
      </section>

      {/* ─── FAQ ─────────────────────────────────────────────────────── */}
      <section id="faq" className="mx-auto max-w-3xl px-6 py-16 md:py-20">
        <h2 className="text-sm uppercase tracking-[0.25em] text-stone-400">
          {t(lang, 'faqTitle')}
        </h2>
        <dl className="mt-8 divide-y divide-stone-800">
          {[
            ['faq1Q', 'faq1A'],
            ['faq2Q', 'faq2A'],
            ['faq3Q', 'faq3A'],
            ['faq4Q', 'faq4A'],
            ['faq5Q', 'faq5A']
          ].map(([qKey, aKey]) => (
            <div key={qKey} className="py-6">
              <dt className="font-medium text-stone-100">{t(lang, qKey)}</dt>
              <dd className="mt-2 text-sm text-stone-400 leading-relaxed">
                {t(lang, aKey)}
              </dd>
            </div>
          ))}
        </dl>
      </section>
    </>
  );
}

function BulletBlock({ title, body }) {
  return (
    <div className="rounded-xl border border-stone-800 bg-stone-900/40 p-5">
      <p className="font-medium text-stone-100">{title}</p>
      <p className="mt-2 text-sm text-stone-400 leading-relaxed">{body}</p>
    </div>
  );
}

function PlanCard({ name, price, cadence, tag, body, featured }) {
  return (
    <div
      className={
        'rounded-2xl border p-5 flex flex-col ' +
        (featured
          ? 'border-alfred-gold bg-stone-900/70 shadow-lg shadow-alfred-gold/10'
          : 'border-stone-800 bg-stone-900/40')
      }
    >
      <p className="text-[11px] uppercase tracking-[0.25em] text-alfred-gold/90">{tag}</p>
      <h3 className="mt-2 text-2xl font-serif text-stone-50">{name}</h3>
      <p className="mt-3 text-3xl font-serif text-stone-50">
        {price}
        <span className="text-sm text-stone-400 font-sans"> / {cadence}</span>
      </p>
      <p className="mt-3 text-sm text-stone-400 leading-relaxed">{body}</p>
    </div>
  );
}

function DiffCompare({ highlight, body }) {
  return (
    <div className="rounded-xl border border-stone-800 bg-stone-900/30 p-5">
      <p className="text-sm text-stone-400 leading-relaxed">{highlight}</p>
      <p className="mt-3 text-stone-100 font-medium leading-snug">{body}</p>
    </div>
  );
}

function TrustBlock({ icon: Icon, title, body }) {
  return (
    <div className="rounded-xl border border-stone-800 bg-stone-900/40 p-5">
      <div className="flex items-center gap-3">
        <Icon className="h-5 w-5 text-alfred-gold" aria-hidden="true" />
        <p className="font-medium text-stone-100">{title}</p>
      </div>
      <p className="mt-2 text-sm text-stone-400 leading-relaxed">{body}</p>
    </div>
  );
}

function TestimonialCard({ name, text }) {
  return (
    <figure className="rounded-xl border border-stone-800 bg-stone-900/30 p-5">
      <blockquote className="text-sm text-stone-200 leading-relaxed">
        &ldquo;{text}&rdquo;
      </blockquote>
      <figcaption className="mt-4 text-xs uppercase tracking-[0.2em] text-alfred-gold/80">
        {name}
      </figcaption>
    </figure>
  );
}
